import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BUSINESS_INFO } from '../constants/legal';

/**
 * AppFooter — business disclosure for every authenticated app page.
 *
 * WAS a 1-line mini footer (VeriPass Design System — April 2026, Section 1-C).
 * It printed only the copyright, a bare telephone number and four policy links.
 *
 * WHY IT GREW (owner request, 2026-09-27)
 * ---------------------------------------
 * 전자상거래법 제10조 requires 상호명 / 대표자명 / 사업자등록번호 / 주소 / 연락처 to be
 * reachable ON the service, not only on the public marketing page. Everything
 * where money is actually discussed — 설정 > 구독, 플랜 업그레이드
 * (/profile/manage-membership), the 환불 tab, the claim-validation screen —
 * lives BEHIND the login, where the landing footer never renders. A Korean
 * reviewer opening a paid screen found none of these fields.
 *
 * Placement was already solved: App.js renders this in all three AppLayout
 * branches, and AppLayout wraps the whole <Routes> block. Only the CONTENT was
 * missing. See the report accompanying this change for the routes AppLayout
 * early-returns out of (notably /pricing), which this footer does NOT reach.
 *
 * ⚠ EVERY VALUE COMES FROM `constants/legal.js`. No string literals here -- the
 * landing footer hardcoded the company name, the CEO and the address beside an
 * interpolated bizRegNo, which is exactly how two disclosures drift apart. A
 * disclosure that disagrees with itself is worse than one that is missing,
 * because both look verified.
 *
 * `ecommerceFilingNo` (통신판매업 신고번호) was supplied on 2026-10-02 and renders
 * between 사업자등록번호 and 주소. The `&&` guard stays: if the constant is ever
 * null the field drops out whole rather than printing an empty label, and a
 * placeholder is never substituted -- a wrong number published on a payment
 * page reads as verified.
 */
function Footer() {
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';

  const links = [
    { label: isKo ? '이용약관' : 'Terms', hash: '#terms' },
    { label: isKo ? '개인정보처리방침' : 'Privacy', hash: '#privacy' },
    { label: isKo ? '환불정책' : 'Refund', hash: '#refund' },
    { label: isKo ? '윤리 가이드라인' : 'Ethics', hash: '#ethics' },
  ];

  // Field order and labels as the owner specified them: 상호명, 대표자명,
  // 사업자등록번호, 주소, 연락처. The filing number sits with the other
  // registration identifiers and drops out entirely if it is ever null.
  const disclosure = [
    {
      key: 'name',
      label: isKo ? '상호명' : 'Company',
      value: isKo ? BUSINESS_INFO.nameKo : BUSINESS_INFO.nameEn,
    },
    {
      key: 'ceo',
      label: isKo ? '대표자명' : 'CEO',
      value: isKo ? BUSINESS_INFO.ceoKo : BUSINESS_INFO.ceoEn,
    },
    {
      key: 'bizRegNo',
      label: isKo ? '사업자등록번호' : 'Business Reg. No.',
      value: BUSINESS_INFO.bizRegNo,
    },
    BUSINESS_INFO.ecommerceFilingNo && {
      key: 'ecommerceFilingNo',
      label: isKo ? '통신판매업 신고번호' : 'E-Commerce Filing No.',
      value: BUSINESS_INFO.ecommerceFilingNo,
    },
    {
      key: 'address',
      label: isKo ? '주소' : 'Address',
      value: isKo ? BUSINESS_INFO.addressKo : BUSINESS_INFO.addressEn,
    },
    {
      key: 'tel',
      // The telephone used to ride along on the © line. It is the 연락처 field
      // now, so it is printed here and NOT there -- one number, once.
      label: isKo ? '연락처' : 'Tel',
      value: BUSINESS_INFO.tel,
    },
  ].filter(Boolean);

  const separator = (
    <span aria-hidden="true" style={{ margin: '0 6px', opacity: 0.6 }}>
      ·
    </span>
  );

  // See the comment at the render site for why these three differ.
  const fieldStyle = (key) => {
    if (key === 'address') return undefined;
    if (key === 'ecommerceFilingNo') return { display: 'inline-block', maxWidth: '100%' };
    return { whiteSpace: 'nowrap' };
  };

  return (
    <footer
      // px-4 at phone width is the 16px gutter; md:px-8 keeps the original
      // 32px inset on desktop. Inline padding would beat the utility classes,
      // so it stays out of `style`.
      className="mt-auto flex flex-col gap-1 px-4 py-4 md:px-8"
      style={{
        borderTop: '1px solid var(--shell-footer-border)',
        backgroundColor: 'var(--shell-footer-bg)',
        fontSize: 11,
        lineHeight: 1.7,
        fontFamily: 'Inter, sans-serif',
        color: 'var(--shell-footer-text)',
      }}
    >
      {/*
        Inline flow, not flex: this is small print and should reflow like a
        sentence. `keep-all` stops Korean breaking mid-word, and
        `overflow-wrap: anywhere` is the backstop for the fields that are
        allowed to wrap. It CANNOT break inside a `white-space: nowrap` span,
        so every nowrap field below must fit the 288px content box of a 320px
        phone on its own, or it scrolls <main className="... overflow-auto">
        sideways.
      */}
      <p style={{ margin: 0, wordBreak: 'keep-all', overflowWrap: 'anywhere' }}>
        {disclosure.map((field, i) => (
          <React.Fragment key={field.key}>
            {i > 0 && separator}
            {/*
              Label and value are kept on one line together -- a 사업자등록번호
              orphaned from its number is unreadable as a disclosure. Two
              deliberate exceptions:
                - the address is the longest field and is allowed to wrap onto
                  a second line rather than be truncated, because a truncated
                  address is not a disclosure;
                - the 통신판매업 신고번호 is the widest one-line field (~255px in
                  en at 11px, against 288px). nowrap would scroll the page
                  sideways once a zoomed-in phone leaves less than that, so it
                  is an inline-block instead: it moves to a new line as one
                  unit, and wraps inside itself only when it alone is wider
                  than a line.
            */}
            <span style={fieldStyle(field.key)}>
              {field.label} {field.value}
            </span>
          </React.Fragment>
        ))}
      </p>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <span>© 2026 VeriPass by WhereAt</span>
        <div className="flex items-center flex-wrap gap-0">
          {links.map((link, i) => (
            <React.Fragment key={link.hash}>
              {i > 0 && <span style={{ color: 'var(--shell-footer-text)', margin: '0 6px' }}>·</span>}
              <Link
                to={`/about${link.hash}`}
                className="transition-colors"
                style={{ color: 'var(--shell-footer-text)', textDecoration: 'none' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--shell-footer-text-hover)')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--shell-footer-text)')}
              >
                {link.label}
              </Link>
            </React.Fragment>
          ))}
        </div>
      </div>
    </footer>
  );
}

export default Footer;
